import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Paper, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AdvertisingLayout from '../../layouts/AdvertisingLayout';

// Import Top Spot icon
import topSpotIcon from '../../assets/Sales/Top Spot.png';

// Import all Top Spot images
import topSpot1 from '../../assets/Sales/Top Spot/316879-Bundall Child Care Centre-269590-N1501-eNewsletters.gif';
import topSpot2 from '../../assets/Sales/Top Spot/316963-Premium-Kinetic-Education.jpg';
import topSpot3 from '../../assets/Sales/Top Spot/Mirani-Medical-Centre-311859-Premium-1.jpg';
import topSpot4 from '../../assets/Sales/Top Spot/Oracare Cleveland Dental & Facial Aesthetics 316874.jpg';

const topSpotImages = [topSpot1, topSpot2, topSpot3, topSpot4];

function TopSpot() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotateKey, setAutoRotateKey] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (isHovering) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // Move by 1 image at a time, loop back to start
        return (prevIndex + 1) % topSpotImages.length;
      });
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isHovering, autoRotateKey]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex === 0 ? topSpotImages.length - 1 : prevIndex - 1;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % topSpotImages.length;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setAutoRotateKey(prev => prev + 1);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setAutoRotateKey(prev => prev + 1);
  };

  // Get current 3 images to display
  const getCurrentImages = () => {
    const images = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % topSpotImages.length;
      images.push(topSpotImages[index]);
    }
    return images;
  };

  return (
    <AdvertisingLayout>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #247AA8 0%, #1E6A92 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Top Spot Exclusivity
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            Always Be Seen First!
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: 6, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Column - Text Content with Icon */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, backgroundColor: 'white', display: 'flex', gap: 3 }}>
                {/* Text Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#333132' }}>
                    Top Spot Exclusivity is now available for Digital School Newsletters. This prime piece of advertising 
                    real estate is open for booking with Standard, Premium and Premium Plus (animated) campaigns.
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#333132' }}>
                    Competing with other similar businesses is tough when you're speaking to the exact same audience. 
                    Why not catch their attention first? This exclusive offer is limited to only one position per digital newsletter.
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, fontWeight: 600, color: '#000000' }}>
                    Top Exclusivity is an additional fee on top of your campaign price, starting from $200 per year.
                  </Typography>

                  {/* Bullet Points */}
                  <Box component="ul" sx={{ pl: 2, mb: 0, listStyleType: 'disc' }}>
                    <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                      Exclusive to Digital Newsletters
                    </Typography>
                    <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                      Available with Standard, Premium & Premium Plus ads
                    </Typography>
                    <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                      Minimum 3 month campaign booking
                    </Typography>
                    <Typography component="li" sx={{ mb: 0, color: '#333132', display: 'list-item' }}>
                      Only one position per digital newsletter
                    </Typography>
                  </Box>
                </Box>

                {/* Top Spot Icon - Right side of text */}
                <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
                  <Box
                    component="img"
                    src={topSpotIcon}
                    alt="Top Spot Exclusive"
                    sx={{
                      maxWidth: '220px',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Pricing Card */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#4A9FCC',
                  color: 'white',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Exclusive Banner */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    backgroundColor: '#FF8C00',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    transform: 'rotate(-3deg)',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    boxShadow: 3,
                    letterSpacing: 0.5
                  }}
                >
                  EXCLUSIVE
                </Box>

                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    mb: 1.5,
                    mt: 0.5,
                    fontSize: '1.5rem'
                  }}
                >
                  TOP SPOT
                </Typography>
                
                <Box sx={{ backgroundColor: 'white', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                  {/* Grey top section - Price */}
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2.5 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#4A9FCC',
                        textAlign: 'center',
                        fontSize: '2.5rem',
                        mb: 0.5
                      }}
                    >
                      $200
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        textAlign: 'center'
                      }}
                    >
                      per year starting price
                    </Typography>
                  </Box>

                  {/* White bottom section - Features */}
                  <Box sx={{ p: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Exclusive Positioning
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        3-12 Month Booking
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Available with Animated Ads
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    navigate('/advertising');
                    setTimeout(() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                  sx={{
                    backgroundColor: 'white',
                    color: '#4A9FCC',
                    fontWeight: 700,
                    py: 1,
                    fontSize: '1rem',
                    border: '2px solid white',
                    borderRadius: 1,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#4A9FCC',
                      color: 'white',
                      borderColor: '#4A9FCC',
                      boxShadow: 'none',
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  Book Now
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Premium Top Spot Ads Section */}
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Paper elevation={0} sx={{ p: 4, backgroundColor: 'white', borderRadius: 2 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              sx={{ mb: 4, fontWeight: 600, color: '#333132', textAlign: 'left' }}
            >
              Premium Top Spot Ads
            </Typography>

            {/* Image Carousel */}
            <Box 
              sx={{ position: 'relative', mb: 4 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Grid container spacing={3} justifyContent="center">
                {getCurrentImages().map((image, idx) => (
                  <Grid item xs={12} md={4} key={idx}>
                    <Paper
                      elevation={3}
                      sx={{
                        overflow: 'hidden',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 6
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={`Top Spot Ad ${idx + 1}`}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Navigation Arrows */}
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: { xs: -10, md: -20 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  boxShadow: 2,
                  '&:hover': {
                    backgroundColor: '#f0f0f0'
                  }
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: { xs: -10, md: -20 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  boxShadow: 2,
                  '&:hover': {
                    backgroundColor: '#f0f0f0'
                  }
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>

            {/* Dot Indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
              {topSpotImages.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: currentIndex === index ? '#247AA8' : '#ccc',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    '&:hover': {
                      backgroundColor: currentIndex === index ? '#247AA8' : '#999'
                    }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Copyright Australian Newsletter Services Pty Ltd 2016-2025 | <a href="/advertising/terms-and-conditions" style={{ color: '#247AA8', textDecoration: 'none' }}>Advertising Terms & Conditions</a> | All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
        </Container>
      </Box>
    </AdvertisingLayout>
  );
}

export default TopSpot;

